import { useMemo } from "react";

import { styled } from "@mui/material/styles";
import { useQuery } from "@tanstack/react-query";
import { FormattedMessage } from "react-intl";
import { Link, useLocation, useNavigate } from "react-router-dom";

import {
  resetKeyQuery,
  useResetFinishMutation,
} from "@/shared/api/gateway/reset/reset.api";
import { EntryContainer } from "@/shared/containers/EntryContainer/EntryContainer";
import { useToggle } from "@/shared/hooks/useToggle";

import { EmailEntryForm } from "./EmailEntryForm";
import { NewPasswordForm } from "./NewPasswordForm";

const SUPPORT_EMAIL = "help@cleverfarm.cz";

const Reset = (): React.ReactElement => {
  const location = useLocation();
  const navigate = useNavigate();
  const { on: mailSent, setOn: setMailSent } = useToggle(false);
  const { on: keyRejected, setOn: setKeyRejected } = useToggle(false);

  const resetKey = useMemo(
    () => new URLSearchParams(location.search).get("key"),
    [location.search],
  );

  const resetKeyValidity = useQuery({
    ...resetKeyQuery(resetKey ?? ""),
    enabled: !!resetKey,
  });

  const resetFinishMutation = useResetFinishMutation();

  const handleNewPassword = (password: string) => {
    if (!resetKey) {
      return;
    }

    resetFinishMutation.mutate(
      { key: resetKey, newPassword: password },
      {
        onSuccess: () => navigate("/login"),
        onError: () => setKeyRejected(),
      },
    );
  };

  const withoutKeyContent = mailSent ? (
    <>
      <Message data-testid="email-sent-message">
        <FormattedMessage id="reset.mail.sent.message" />
      </Message>
      <CenteredLink to="/login">
        <FormattedMessage id="reset.linkToLogin" />
      </CenteredLink>
    </>
  ) : (
    <EmailEntryForm onMailSent={setMailSent} />
  );

  const withKeyContent =
    resetKeyValidity.data && !keyRejected ? (
      <NewPasswordForm
        onNewPassword={handleNewPassword}
        pending={resetFinishMutation.isPending}
      />
    ) : (
      <Message data-testid="invalid-key">
        <FormattedMessage id="reset.key.not.valid" />
      </Message>
    );

  const errorContent = (
    <Message>
      <FormattedMessage id="reset.error" />
      <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
    </Message>
  );

  const content = () => {
    if (resetKeyValidity.isError) {
      return errorContent;
    }

    return resetKey ? withKeyContent : withoutKeyContent;
  };

  return (
    <EntryContainer isFetching={resetKeyValidity.isFetching}>
      {content()}
    </EntryContainer>
  );
};

const Message = styled("div")(({ theme }) => ({
  textAlign: "center",
  margin: theme.spacing(2, 0),
}));

const CenteredLink = styled(Link)({
  display: "block",
  textAlign: "center",
  width: "100%",
});

export { Reset };
